import { createSelector, createSlice } from '@reduxjs/toolkit'

import {getRowAt} from 'store/functions/inputFunctions.js';
import {findOutOfRangeLocations} from 'store/functions/coordinateSystem.js';
import { coordinateSystemMethodChangeMessage } from 'store/functions/costMethods.js';
import { reconcileTravelCostTyping } from 'store/functions/travelCostTyping.js';
import is_iterable from 'functions/general/is_iterable.js'
import { SAVED_RUNS_PANEL } from 'functions/general/panelNames';
import undoable from 'redux-undo'
import CostMatrixResponse from 'open-api-gen/model/CostMatrixResponse.js';

import {
  addRowAction,
  addRowHelper,
  applyResultAmendmentHelper,
  clearResultsHelper,
  clearTableDataHelper,
  compareValues,
  decipherUploadedTravelCostsReturnHelper,
  deleteRowHelper,
  editRowHelper,
  getDefaultValue,
  keepOnlyDepotsHelper,
  revertResultAmendmentsHelper,
  travelCostUpdate,
  validateTable,
  validateTimeUnitViewability
} from "store/reducers/helpers";

import {
  initializeData
} from "store/reducers/initializers/stateInitializer.js";

import {
  generateUUID, 
  initializeGeoJson,
  addRowGeoJsonHelper,
  deleteRowGeoJsonHelper,
  addGeoJsonTripHelper,
  addGeoJsonResults,
  updateTableHelper,
  onLocationDataChangeGeoJsonHelper,
  clearGeoJsonResults
} from 'store/functions/geoJsonFunctions.js';
import {
  ID_CLEARED_BY_UPDATE,
  SAVED_AS_NEW_AFTER_UPDATE_MESSAGE,
  isSameOpenScenario,
  scenarioIdAfterLoad,
  stampScenarioHistory,
} from 'store/functions/scenarioIdentity.js';


// Import thunks from separate files
import {
  fetchAddress,
  submitMessage,
  joinEmailList,
  submitBugReport,
  downloadSampleTable,
  enqueueOptimization,
  solveOptimization,
  dequeueRun,
  fetchRunResults,
  deleteOptimizationResult,
  renameTrial,
  fetchSavedScenario,
  deleteSavedScenario,
  saveScenario,
  renameScenario,
  fetchTravelCosts,
  decipherUploadedTravelCosts
} from 'store/reducers/thunks';

import { AUTO_CALCULATED_COSTS_MESSAGE, calculatedTravelCostProblems, hasAnyComputedTravelCost, keepExistingTravelCosts, preferCalculatedTravelCosts, rescaleCalculatedCosts } from 'store/functions/travelCostResults';

import {
  DEFAULT_LOGIN_DATA,
  DEFAULT_NOTIFICATIONS,
  INITIAL_DATA,
  STATIC_MODEL_SETTINGS,
  STATIC_TABLE_SETTINGS
} from 'store/reducers/constants/constants.js';

// Re-export thunks for backward compatibility
export { fetchAddress, submitMessage, joinEmailList, submitBugReport, downloadSampleTable, enqueueOptimization, solveOptimization, dequeueRun, fetchRunResults, deleteOptimizationResult, renameTrial, fetchSavedScenario, deleteSavedScenario, saveScenario, renameScenario, fetchTravelCosts, decipherUploadedTravelCosts };

const INIT_DATA = initializeData();

const failSolveOptimizationHelper = (state) => {
    const newState = {...state}; 
    // add a notification
    const newNotification = {
      'type': 'error',
      'message': 'Optimization failed with unknown error. Please try again or contact support.'
    }
    newState['notifications']['optimization'].push(newNotification)
    state = newState;
  }

const TIME_WINDOW_FIELDS = [
  { fieldName: 'pickupExact',  useKey: 'usePickupExact',  isExact: true },
  { fieldName: 'pickupStart',  useKey: 'usePickupStart',  isExact: false },
  { fieldName: 'pickupEnd',    useKey: 'usePickupEnd',    isExact: false },
  { fieldName: 'dropoffExact', useKey: 'useDropoffExact', isExact: true },
  { fieldName: 'dropoffStart', useKey: 'useDropoffStart', isExact: false },
  { fieldName: 'dropoffEnd',   useKey: 'useDropoffEnd',   isExact: false },
];

const applyTimeFieldVisibility = (state, tableName, fieldName, useKey, isExact, newTimeUnit) => {
  const tableTimeColumnNames = STATIC_TABLE_SETTINGS[tableName]['timeWindows'];
  const tableTimeSettings = state.generalSettings.time[tableName];
  if (!tableTimeSettings || !tableTimeSettings[fieldName]) return;
  const useField = state.inputData.present.generalSettings.time[tableName][useKey];
  if (newTimeUnit === 'integer') {
    state.tableSettings[tableName].isViewable[tableTimeColumnNames[fieldName]['columnNameInt']] = !!useField;
    if (isExact && useField) {
      state.tableSettings[tableName].isViewable[tableTimeColumnNames[fieldName]['columnNameTime']] = false;
    }
  } else {
    state.tableSettings[tableName].isViewable[tableTimeColumnNames[fieldName]['columnNameTime']] = !!useField;
    if (isExact && useField) {
      state.tableSettings[tableName].isViewable[tableTimeColumnNames[fieldName]['columnNameInt']] = false;
    }
  }
};

/*
Leaving example mode for modeling. When the prior mode was 'example', the open
scenario's id is cleared, so the next Save creates the user's own scenario rather
than landing on the example (or on whatever was open before it), and the
top-level scenarioName is cleared. Shared by setActiveMode and
startTemplateFromExample. See store/functions/scenarioIdentity.js.
*/
const enterModelingMode = (state) => {
  const priorMode = state.activeMode;
  state.activeMode = 'modeling';
  if (priorMode === 'example') {
    state.generalSettings.scenarioName = null;
    delete state.generalSettings.scenarioId;
    if (state.generalSettings.scenario) {
      state.generalSettings.scenario.scenarioId = null;
      delete state.generalSettings.scenario[ID_CLEARED_BY_UPDATE];
    }
  }
};

/** The start of the message when a scenario rename fails. */
export const RENAME_SCENARIO_FAILED = "The scenario wasn't renamed.";
/** The start of the message when a run rename fails. */
export const RENAME_RUN_FAILED = "The run wasn't renamed.";

/**
 * A failed rename, in words: the backend's own message when it sent one, else
 * "Please try again."
 *
 * @param {string} lead - RENAME_SCENARIO_FAILED or RENAME_RUN_FAILED
 * @param {{code?: string, message?: string}} [error] - the thunk's serialized error
 * @returns {string}
 */
export function renameFailedMessage(lead, error) {
  const detail = error && error.code && error.message ? error.message : 'Please try again.';
  return `${lead} ${detail}`;
}

/*
An example's travel model (generalSettings.costs.averageSpeed and
roadCircuityFactor, published by backend #361) describes that example's city and
fleet. The site has no place to see or edit it, and a scenario saves it for good,
so it must not follow the person into a scenario of their own: "Build from
scratch" on a card, Clear all, or a switch of coordinate system each drop it, and
the backend's defaults apply (on x-y, travel time equals distance). "Use as
template" keeps it on purpose: same city, same fleet (startTemplateFromExample).
*/
const dropExampleTravelModel = (state) => {
  if (state && state.generalSettings && state.generalSettings.costs) {
    delete state.generalSettings.costs.averageSpeed;
    delete state.generalSettings.costs.roadCircuityFactor;
  }
};

export const inputSlice = createSlice({
  name: 'inputData',
  initialState: {...INIT_DATA},
  reducers: {
    addGeoJsonTrip: (state, action) => {
        const trip = action.payload.trip;
        const uuid = action.payload.uuid;
        if (trip) {
          if (trip.geometry.type === 'LineString') {
            const coordinates = trip.geometry.coordinates;
            const fromValuesRow = getRowAt(state['data'], 'locations', ['longitude', 'latitude'], coordinates[0])
            let fromLocation;
            let fromUuid;
            if (fromValuesRow.exists) {
              fromLocation = fromValuesRow.value['locationName'];
              fromUuid = fromValuesRow.value['UUID'];
            }
            const toValuesRow = getRowAt(state['data'], 'locations', ['longitude', 'latitude'], coordinates[1])
            let toLocation;
            let toUuid;
            if (fromValuesRow.exists) {
              toLocation = toValuesRow.value['locationName'];
              toUuid = toValuesRow.value['UUID'];
            }

            if (fromLocation && toLocation) {
              const newRow = {'fromLocationName': fromLocation, 'toLocationName': toLocation, 
                'fromLatitude': coordinates[0][1],
                'fromLongitude': coordinates[0][0],
                'toLatitude': coordinates[1][1],
                'toLongitude': coordinates[1][0],
                'toLocationId': toUuid,
                'fromLocationId': fromUuid
              }
              
              const newState = addRowHelper({state: state, newRow:newRow, uuid:uuid, tableName:'trips'});
              state['data'] = newState['data'];
            }
            state['geoJson'] = addGeoJsonTripHelper(state['geoJson'],  uuid, toUuid, fromUuid, trip)
          }
        }
    },

    onLocationDataChange: (state, action) => {
      const updatedState = action.payload.newState;
      const tableName = action.payload.tableName;
      const idField = state['data'][tableName]['columnName'][0]
      if (updatedState.cellEdit) {

        const uuid = updatedState.cellEdit.rowId;
        const columnName = updatedState.cellEdit.dataField;
        const newValue = updatedState.cellEdit.newValue;
        const updatedInputState = editRowHelper(state, uuid, columnName, tableName, newValue);
        state['data'] = updatedInputState['data'];
      }

      if (updatedState.sortField) {
        const sortField = updatedState.sortField;
        const sortOrder = updatedState.sortOrder;

        let ascending = true;
        if (sortOrder === 'desc') {
          ascending = false;
        } 
        state['data'][tableName] = state['data'][tableName].sort((a, b) => compareValues(a[sortField], b[sortField], ascending));
        for (let i = 0; i < state['data'][tableName].length; i++) {
          state['data'][tableName][i][idField] = i + 1;
        }
      }

      state['geoJson'] = onLocationDataChangeGeoJsonHelper(state['geoJson'], action);
    },
    deleteRowGeoJson: (state, action) => {
      state['geoJson'] = deleteRowGeoJsonHelper(state['geoJson'], action.payload.uuid, action.payload.tableName);
    },
    deleteRow: (state, action) => {
      const uuid = action.payload.uuid;
      if (!uuid) {
        return
      }
      const tableName = action.payload.tableName;
      state = deleteRowHelper(state, tableName, uuid)
    },

    clearNotifications(state, action) {
      if (action.payload) {
        if (action.payload.type === 'routine') {
          state['notifications'] = {...state['notifications'], 'routine': DEFAULT_NOTIFICATIONS['routine']}
        }
        else if (action.payload.type === 'warning') {
          state['notifications'] = {...state['notifications'], 'warning': []}
        }
        else if (action.payload.type === 'optimization') {
          state['notifications'] = {...state['notifications'], 'optimization': DEFAULT_NOTIFICATIONS['optimization']}
        }
      }
      else {
      state['notifications'] = {...DEFAULT_NOTIFICATIONS};
      }
    },

    setActiveMode(state, action) {
      const activeMode = action.payload.activeMode
      if (activeMode === 'example') {
        state.activeMode = 'example';
      }

      else if (activeMode === 'model' || activeMode === 'modeling') {
        enterModelingMode(state);
      }
      
      else {
        throw new Error('Unexpected mode received in setActiveMode')
      }
    },
    /*
    "Use as template" on an example, as a single undoable step: keep the example's
    depots, fleet and settings; drop its results, stops and trips; detach it from
    the example; and leave example mode.

    One action rather than a sequence of dispatches so that one Undo puts the
    example back whole. As separate steps, the last one to undo was the slug write,
    so a single Undo restored the example's slug onto an otherwise-cloned scenario.

    The scenario is no longer the example's saved instance, so its identity is
    cleared as well as the slug:
    - scenario.scenarioId -> null. Rename acts on this id; left in place it would
      rename the example's instance (which the backend rejects, as the visitor does
      not own it) instead of saving the user's own copy.
    - scenario.lastSaved -> null. Save skips its name prompt and the action bar
      hides its unsaved-changes dot whenever lastSaved is set.
    scenarioChange is kept: it only drives the map's fit-to-bounds.

    The example's cost settings are kept too, travel model included
    (generalSettings.costs.averageSpeed / roadCircuityFactor, backend #361): a
    template is the same city and the same fleet, so new stops should cost on the
    example's scale. "Build from scratch" is the one that drops it (see
    fetchSavedScenario.fulfilled).

    Payload: { scenarioName }.
    */
    startTemplateFromExample(state, action) {
      const scenarioName = action.payload && action.payload.scenarioName;
      clearResultsHelper(state, true);
      keepOnlyDepotsHelper(state);
      enterModelingMode(state);
      if (!state.generalSettings.scenario) {
        state.generalSettings.scenario = {};
      }
      const scenario = state.generalSettings.scenario;
      if (scenarioName) {
        scenario.scenarioName = scenarioName;
      }
      scenario.exampleSlug = null;
      scenario.scenarioId = null;
      scenario.lastSaved = null;
      // Defensive: the "Depot?" column is on by default and has no toggle today,
      // so this normally changes nothing. If it is ever hidden, the kept rows
      // would look like sample stops that failed to clear. Only where the model
      // has that column (pickup-delivery disables it).
      const locationsDisabled = STATIC_MODEL_SETTINGS[state.activeModelType]?.input?.disabled?.locations || {};
      const locationViewability = state.tableSettings?.locations?.isViewable;
      if (locationViewability && !locationsDisabled.isDepot
          && (state.data.locations || []).some((row) => row.isDepot)) {
        locationViewability.isDepot = true;
      }
    },
    clearColumn(state, action) {
      const columnName = action.payload.columnName;
      const tableName = action.payload.tableName;
      let newState = {...state}
      state.data[tableName].forEach((elem) => {
        const uuid = elem['UUID'];
        newState = editRowHelper(newState, uuid, columnName, tableName, null)
      })
    },
    addRow: (state, action) => {
      addRowAction(state, action)
    },
    addRowFromMap: (state, action) => {
      addRowAction(state, action);
    },
    login: (state, action) => {
      state.loginData['loggedIn'] = true;
      const newLogin = action.payload.newLogin;
      const loginSource = action.payload.loginSource;
      state.loginData['logInSource'] = loginSource;

      if (loginSource === 'clerk') {
          const prevUserId = state.loginData['userId'];
          // Replacing an anonymous id (a throwaway client UUID, not a Clerk sub
          // like `user_...`) with the signed-in id: remember it so we can ask the
          // backend to re-own the anonymous runs to this account (see claim_runs).
          if (prevUserId && prevUserId !== newLogin['id'] && !String(prevUserId).startsWith('user_')) {
            state.loginData['pendingClaimUserId'] = prevUserId;
          }
          state.loginData['userId'] = newLogin['id']
      }
      if (!state.loginData.plan) {
        state.loginData['plan'] = 'free'
      }

    },
    // Cleared once the anonymous runs have been successfully re-owned.
    clearPendingClaim: (state) => {
      state.loginData['pendingClaimUserId'] = null;
    },
    setUserLimits: (state, action) => {
      let newState;
      if (action.payload.newLimits) {
        const newLimits = {limits: action.payload.newLimits};
        const newLoginData = {loginData: {...state.loginData, ...newLimits}};
        newState = {...state, 
          ...newLoginData
        }
      } else {
        newState = {...state, loginData : {
          ...state.loginData,
          'limits': DEFAULT_LOGIN_DATA.limits}
        }
      }
      return newState;
    },
    setUserPlan: (state, action) => {
      const newPlan = action.payload.plan;
      state.loginData['plan'] = newPlan;
    },
    logoutUser: (state) => {
      const newState = {...state, loginData: {...DEFAULT_LOGIN_DATA}};
      newState.loginData['loggedIn'] = false;
      return newState;
    },

    addRowGeoJson: () => {},

    updateTable: (state, action) => {

      const tableName = action.payload.tableName;
      const updateColumnName = action.payload.updateColumnName;
      let uuid = action.payload.uuid;
      if (!uuid) {
        const rowNum = action.payload.row;
        uuid = state.data[tableName][rowNum]['UUID']
      }

      if (!uuid) {
        const err_msg = 'no UUID provided in the dispatch payload for updateTable.';
        throw new Error(err_msg);
      }
      const newValue = action.payload.newValue;
      state = editRowHelper(state, uuid, updateColumnName, tableName, newValue)
      state['geoJson'] = updateTableHelper(state['geoJson'], tableName, updateColumnName, uuid, newValue)

    },
    /*
    Puts calculated costs made at an old multiplier onto the one in use now (the
    Run dialog's "Rescale those costs and solve"; see mixedCostScales). Only the
    rows named, and only those still calculated at an old scale, so a value
    typed since the dialog opened is left alone. Undoable like any table edit.
    */
    rescaleCalculatedTravelCosts: (state, action) => {
      state.data.travel_costs = rescaleCalculatedCosts(
        state.data.travel_costs,
        action.payload.uuids,
        state.generalSettings.costs,
        state.data.vehicle_types,
      );
    },
    updateTableMulti: (state, action) => {
      const tableName = action.payload.tableName;
      const updateColumnNames = action.payload.updateColumnNames;
      const newValues = action.payload.newValues;
      const uuids = action.payload.uuids;
      let newState = {...state};
      newValues.forEach((newValue, idx) => {
        const updateColumnName = updateColumnNames[idx];
        let uuid = uuids[idx];
        if (!uuid) {
          const rowNum = action.payload.row;
          uuid = state.data[tableName][rowNum]['UUID']
        }

        if (!uuid) {
          const err_msg = 'no UUID provided in the dispatch payload for updateTable.';
          throw new Error(err_msg);
        }
        newState = editRowHelper(newState, uuid, updateColumnName, tableName, newValue)
        newState['geoJson'] = updateTableHelper(newState['geoJson'], tableName, updateColumnName, uuid, newValue)
    
      });
      state = newState;
    },
    addNotification: (state, action) => {
      const notificationMsg = action.payload.notificationMsg;
      if (!notificationMsg) {
        throw new Error('No message provided in addNotification (use parameter notificationMsg)');
      }
      const notificationType = action.payload.notificationType;
      if (!notificationType) {
        throw new Error('No type provided in addNotification (use parameter notificationType)');
      }
      const notificationAction = action.payload.notificationAction;
      const routineType = action.payload.isNonRoutine ? 'warning': 'routine';
      state['notifications'][routineType].push({
        'type': notificationType,
        'message': notificationMsg,
        'action': notificationAction,
        'payload': action.payload.notificationActionPayload,
        'proceedText': action.payload.proceedText,
        // NotificationManager reads `upgrade` to add a "See plans" button and
        // keep the snackbar up until the user acts, but this reducer dropped the
        // field — so that prompt was unreachable through addNotification, and
        // only the travel-cost path (which pushes onto state directly) could
        // ever show it. Passed through so any caller can offer the upgrade.
        'upgrade': action.payload.upgrade,
        // A rememberedSwitches key. When set, NotificationManager offers
        // "Don't show again", which flips that switch so the caller can stop
        // raising this notification. For confirmations that fire on a repeated
        // action — every map click, say — where the first one helps and the
        // thirtieth is noise.
        'dismissSwitch': action.payload.dismissSwitch,
      })
    },
    updateActiveModelType: (state, action) => {
      const modelType = action.payload.modelType;
      const activeResultsTable = STATIC_MODEL_SETTINGS[state.activeModelType]['resultsTable'];
      const hasResults = Boolean(state.data[activeResultsTable] && state.data[activeResultsTable].length);
      const isModelChange = modelType !== state.activeModelType;

      // Ask BEFORE destroying anything.
      //
      // This check used to run after clearResultsHelper, so it inspected the
      // results it had just deleted, always found none, and fell through to the
      // else branch. The warning below could not fire on any path that passed
      // clearResults — which is every path the model-builder wizard uses — so a
      // visitor who had solved lost the run with nothing said.
      //
      // `confirmed` is what stops this looping: WarningDialogActionRequired
      // re-dispatches this same payload when the user accepts, and without a
      // marker that re-dispatch would raise the warning again.
      if (isModelChange && hasResults && state.activeMode !== 'example' && !action.payload.confirmed) {
          const newNotification = {
            'type': 'warning',
            'message': `Switching model type will clear results from your latest optimization run. Your previous runs are still in the "${SAVED_RUNS_PANEL}" panel in the sidebar.`,
            'payload': {...action.payload, clearResults: true, confirmed: true},
            'cancelPayload': {modelType: state.activeModelType, clearResults: false},
            'action': 'updateActiveModelType',
            'clearResults':true,
            ...action.payload
          }
          state['notifications']['warning'].push(newNotification)
          return;
      }

      // Only a real model change invalidates results. Re-selecting the model you
      // are already on must not throw the run away — the wizard does exactly
      // that every time it restarts at question 1 and the visitor clicks Select.
      if (action.payload.clearResults && isModelChange) {
        clearResultsHelper(state, action.payload.resetMapLayers)
      }

      state.activeModelType = modelType;
      state['notifications'] = {...DEFAULT_NOTIFICATIONS};

      if (STATIC_MODEL_SETTINGS[modelType] && !STATIC_MODEL_SETTINGS[modelType]['useLocationDepot']) {
        state.tableSettings.locations.isViewable = {...state.tableSettings.locations.isViewable, 'Depot': false}
      } else {
        state.tableSettings.locations.isViewable = {...state.tableSettings.locations.isViewable, 'Depot': true}
      }

      if (STATIC_MODEL_SETTINGS[modelType]?.toggleFields) {
        Object.keys(STATIC_MODEL_SETTINGS[modelType].toggleFields).forEach((tableName) => {
          Object.keys(STATIC_MODEL_SETTINGS[modelType].toggleFields[tableName]).forEach((columnName) => {
            const offToggleColumn = STATIC_TABLE_SETTINGS[tableName]['isToggleable'] && STATIC_TABLE_SETTINGS[tableName]['isToggleable'][columnName]['correspondingColumn']
            if (!state.tableSettings[tableName]['isViewable'][columnName]) {
              state.tableSettings[tableName]['isViewable'][offToggleColumn] = STATIC_MODEL_SETTINGS[modelType].toggleFields[tableName][columnName]
            }
          })
        })
      }

    },

    /**
     * Set the scenario's coordinate system ('geographic' | 'cartesian').
     *
     * A dedicated action rather than a plain updateGeneralSetting because the
     * write has side effects: cost methods must stay valid for the coordinate
     * system, and switching to geographic can invalidate existing coordinates.
     *
     * Payload: { coordinateSystem, force? }. Switching cartesian → geographic
     * with coordinates outside lat/lng bounds raises a confirm-style warning
     * instead of applying; the notification re-dispatches with `force: true`,
     * which clears those coordinates (they cannot exist as lat/lng). The
     * action is undoable, so that clearing can be reversed with Undo.
     */
    updateCoordinateSystem: (state, action) => {
      const validSystems = ['geographic', 'cartesian'];
      const coordinateSystem = action.payload?.coordinateSystem;
      if (!validSystems.includes(coordinateSystem)) {
        throw new Error(`Invalid coordinateSystem: ${coordinateSystem}. Must be one of: ${validSystems.join(', ')}`);
      }

      const current = state.generalSettings?.mapping?.coordinateSystem ?? 'geographic';
      if (current === coordinateSystem) return;

      // Plane coordinates are routinely outside ±90/±180 and simply cannot be
      // expressed as latitude/longitude, so switching has to discard them.
      // That is destructive, so it is always confirmed first.
      if (coordinateSystem === 'geographic') {
        const offenders = findOutOfRangeLocations(state.data?.locations);
        if (offenders.length > 0) {
          if (!action.payload?.force) {
            const count = offenders.length;
            const plural = count === 1 ? '' : 's';
            // Stored shape must match what addNotification writes and
            // WarningDialogActionRequired reads: {type, message, action, payload}.
            state['notifications']['warning'].push({
              type: 'warning',
              message:
                `${count} location${plural} ${count === 1 ? 'has' : 'have'} coordinates outside the valid latitude/longitude range. ` +
                `Switching to a geographic map will permanently clear ${count === 1 ? 'that coordinate' : 'those coordinates'} — ` +
                `the location${plural} will remain but must be given new latitude/longitude values before you can solve. ` +
                `Any trips connecting ${count === 1 ? 'it' : 'them'} will be removed from the map. Use Undo to reverse this.`,
              action: 'updateCoordinateSystem',
              // roadNetworkAllowed travels with the payload: this warning is
              // re-dispatched verbatim when the person confirms, and without it
              // that path would name a method their calculations never use.
              payload: { coordinateSystem, force: true, roadNetworkAllowed: action.payload.roadNetworkAllowed },
              proceedText: `Clear ${count} coordinate${plural} and switch`,
            });
            return;
          }

          // Confirmed: drop the unusable coordinates. The rows themselves are
          // kept — names, demand, and time windows are still meaningful; only
          // the position is gone, and normal row validation will flag it as a
          // missing required field.
          for (const row of offenders) {
            row.latitude = null;
            row.longitude = null;
            // Removes the point and any trips drawn to/from it, which can no
            // longer be placed.
            state['geoJson'] = deleteRowGeoJsonHelper(state['geoJson'], row.UUID, 'locations');
          }
          // Mutates the draft in place; the return value is the same object.
          validateTable(state, 'locations');
        }
      }

      state.generalSettings.mapping.coordinateSystem = coordinateSystem;
      // A travel model describes distances in the old system (miles on a map, or
      // the plane's own units); carried across, it rescales every travel time.
      dropExampleTravelModel(state);

      // Keep cost methods valid for the coordinate system: geographic-only
      // methods (geo/driving) can't run on a plane, and euclidean can't run on
      // a globe. Manhattan exists in both, so it is preserved either way.
      const costs = state.generalSettings?.costs;
      if (costs) {
        const before = { distanceMethod: costs.distanceMethod, durationMethod: costs.durationMethod };
        if (coordinateSystem === 'cartesian') {
          if (costs.distanceMethod !== 'manhattan') costs.distanceMethod = 'euclidean';
          if (costs.durationMethod !== 'manhattan') costs.durationMethod = 'euclidean';
        } else {
          if (costs.distanceMethod === 'euclidean') costs.distanceMethod = 'geo';
          if (costs.durationMethod === 'euclidean') costs.durationMethod = 'driving';
        }
        // Changing this setting quietly rewrote how travel costs are measured.
        // Say which methods changed, and why, rather than leaving someone to
        // find it in Cost settings later.
        // roadNetworkAllowed comes from the dispatcher (the coordinate-system
        // control reads selectRoadNetworkAccess, as fetchTravelCosts does), so
        // this and the straight-line notice describe the same calculation.
        const message = coordinateSystemMethodChangeMessage(
          before, costs, coordinateSystem, action.payload.roadNetworkAllowed
        );
        if (message) {
          state['notifications']['routine'].push({ type: 'info', message });
        }
      }
    },

    updateModelSetting: (state, action) => {
      let newState = {...state}
      const updateField = action.payload.field;
      const updateValue = action.payload.value;
      const modelType = action.payload.modelType;
      newState['modelSettings'][modelType][updateField] = updateValue;
      state = newState;
    },
    updateMapMode: (state, action) => {
      const validMapModes = ['location', 'trip', 'view'];
      const mapMode = action.payload;
      if (!validMapModes.includes(mapMode)) {
        throw new Error(`Invalid mapMode: ${mapMode}. Must be one of: ${validMapModes.join(', ')}`);
      }
      let newState = {...state}
      newState['generalSettings']['mapping']['mapMode'] = mapMode;
      state = newState;
    },
    updateGeneralSetting: (state, action) => {
      let newState = {...state}
      const settingName = action.payload.settingName;
      const subSettingName = action.payload.subSettingName;
      const subSubSettingName = action.payload.subSubSettingName;
      const updateValue = action.payload.value;
      if (!subSettingName) {
        newState['generalSettings'][settingName] = updateValue;
      } else if (!subSubSettingName) {
        newState['generalSettings'][settingName][subSettingName] = updateValue;
      } else {
        newState['generalSettings'][settingName][subSettingName][subSubSettingName] = updateValue;
      }

      if (settingName === 'costs') {
        if (subSettingName === 'costMetric') {
          if (updateValue === 'distance') {
            STATIC_TABLE_SETTINGS['travel_costs']['summary']['subFields'][2] = 'distance'
          }
          if (updateValue === 'duration') {
            STATIC_TABLE_SETTINGS['travel_costs']['summary']['subFields'][2] = 'duration'
          }
        }
        newState = validateTable(newState, 'travel_costs');
      }

      state = {...newState};      
    },
    updateTableSetting: (state, action) => {
      let newState = {...state};
      const tableName = action.payload.tableName;
      const settingName = action.payload.settingName;
      const newValue = action.payload.newValue;
      const columnName = action.payload.columnName;
      if (is_iterable(newValue)) {
        if (!is_iterable(columnName)) {
          throw new Error('Received iterable newValue. Expecting columnName to be iterable as well');
        }
        columnName.forEach((colName, idx) => {
          const newVal = newValue[idx];
          newState['tableSettings'][tableName][settingName][colName] = newVal;
        })
      }
      else {
        if (columnName) {
          newState['tableSettings'][tableName][settingName][columnName] = newValue;
        } else {
          newState['tableSettings'][tableName][settingName] = newValue;
        }
      }
      state = newState;
    },
    toggleCostVehicleTypeDependence: (state, action) => {
      let newState = {...state};
      newState.generalSettings.costs.vehicleTypeDependence = action.payload;
      newState.tableSettings['travel_costs']['isViewable']['vehicleType'] = action.payload;
      newState.data.travel_costs = [];
      newState = travelCostUpdate(newState);
      state = newState;
    },
    /*
    Link travel-cost rows to the vehicle types they name, and put the display
    flag back in step with the rows.

    Dispatched before a solve, and only when there is something to fix
    (travelCostTypingNeedsRepair, checked against the plain state so the common
    no-op never builds a draft over a 23k-row table). The rows on the wire are
    repaired either way — getTravelCosts is the backstop — but a person whose
    matrix was quietly relinked should see the same table we sent, and be told.
    */
    repairTravelCostTyping: (state) => {
      reconcileTravelCostTyping(state, { notify: true });
    },
    toggleTableSetting: (state, action) => {
      let newState = {...state};
      const tableName = action.payload.tableName;
      const settingName = action.payload.settingName;
      const columnName = action.payload.columnName;
      if (is_iterable(columnName)) {
        if (!is_iterable(columnName)) {
          throw new Error('Received iterable newValue. Expecting columnName to be iterable as well');
        }
        columnName.forEach((colName) => {
          newState['tableSettings'][tableName][settingName][colName] = !newState['tableSettings'][tableName][settingName][colName];
        })
      }
      else {
        if (columnName) {
          newState['tableSettings'][tableName][settingName][columnName] = !newState['tableSettings'][tableName][settingName][columnName];
        } else {
          newState['tableSettings'][tableName][settingName] = !newState['tableSettings'][tableName][settingName] ;
        }
      }
      state = newState;
    },
    clearAllTableData: (state, action) => {
      let newState = JSON.parse(JSON.stringify(state));
      if (action.payload.clearResults) {
        const resetMapLayers = action.payload.resetMapLayers
        clearResultsHelper(newState, resetMapLayers)
      }

      const activeResultsTable = STATIC_MODEL_SETTINGS[newState.activeModelType] && STATIC_MODEL_SETTINGS[newState.activeModelType]['resultsTable'];
      if (activeResultsTable && newState.data[activeResultsTable] && newState.data[activeResultsTable].length > 0) {
        const newNotification = {
          'type': 'warning',
          'message': `Clearing table data will clear results from your latest optimization run. Your previous runs are still in the "${SAVED_RUNS_PANEL}" panel in the sidebar.`,
          'payload': action.payload,
          'action': 'clearAllTableData'
        }
        newState.notifications.warning.push(newNotification)
      }

      else {   
        Object.keys(newState.data).forEach((tableName) => {
          newState['data'][tableName] =  [];
          newState['geoJson'] = initializeGeoJson();
        })
        newState = initializeData(newState)
        // Clearing everything starts the person's own scenario, so an example's
        // travel model (reached by "Edit scenario directly", or by opening an
        // example and moving to another model page) doesn't come along. See
        // dropExampleTravelModel.
        dropExampleTravelModel(newState);
      }
      return newState;

    },
    clearTableData: (state, action) => {
      const tableName = action.payload.tableName;

      if (action.payload.clearResults) {
        clearResultsHelper(state, action.payload.resetMapLayers)
      }

      if (action.payload.clearExisting) {
        clearTableDataHelper(state, action.payload.tableName)
        
      }

      const activeResultsTable = state.activeModelType && STATIC_MODEL_SETTINGS[state.activeModelType]['resultsTable'];
      if (activeResultsTable && state.data[activeResultsTable] && state.data[activeResultsTable].length > 0) {
        const newNotification = {
          'type': 'warning',
          'message': `Clearing table data will clear results from your latest optimization run. Your previous runs are still in the "${SAVED_RUNS_PANEL}" panel in the sidebar.`,
          'payload': action.payload,
          'action': 'clearTableData'
        }
        state.notifications.warning.push(newNotification)
      }

      else {
        state = clearTableDataHelper(state, tableName);
      }
    },
    clearResults: (state, action) => {
      const resetMapLayers = action.payload && action.payload.resetMapLayers;
      state = clearResultsHelper(state, resetMapLayers);
    },
    // Post-solve route editing: commit one evaluated amendment (payload:
    // {amendment, overriddenViolationIds}) / restore the pristine solver
    // baseline. Both are in the redux-undo allowlist, so individual edits are
    // undoable like any table change.
    applyResultAmendment: (state, action) => {
      applyResultAmendmentHelper(state, action.payload);
    },
    revertResultAmendments: (state) => {
      revertResultAmendmentsHelper(state);
    },
    setScenario: (state, action) => {
      const scenarioData = action.payload['scenarioData'];
      state = initializeData(scenarioData);
    },
    addFilter: (state, action) => {
      let newState = {...state};
      const field = action.payload.field;
      const value = action.payload.value;
      if (Array.isArray(value)) {
        if (field === 'vehicleId') {
          value.forEach((filterValue) => {
            newState.geoJsonFilter['vehicleIds'].push(String(filterValue));
          })
        }
      } else {
        if (field === 'vehicleId') {
          newState.geoJsonFilter['vehicleIds'].push(String(value));
        }
      }

      state = newState;
    },
    removeFilter: (state, action) => {
        const field = action.payload.field;
        const value = action.payload.value;
        if (Array.isArray(value)) {
          if (field === 'vehicleId') {
            value.forEach((filterValue) => {
              state.geoJsonFilter['vehicleIds'] = state.geoJsonFilter['vehicleIds'].filter(function(item) { 
                return (String(item) !== String(filterValue));  
            });
            })
          }
        } else {
          if (field === 'vehicleId') {
              state.geoJsonFilter['vehicleIds'] = state.geoJsonFilter['vehicleIds'].filter(function(item) { 
                  return (String(item) !== String(value));  
              });
          }    
        }      
    },
    setMapMode: (state, action) => {
      const mapMode = action.payload.mapMode;
      state.generalSettings.mapping.mapMode = mapMode;
      if (action.payload.clearResults) {
        clearResultsHelper(state, action.payload.resetMapLayers)
      }
    },
    updateFilter: (state, action) => {
        const filterName = action.payload.filterName;
        const newValue = action.payload.newValue;
        state.geoJsonFilter[filterName] = newValue;
    },
    updateCostSettings: (state, action) => {
      const settingName = action.payload.settingName;
      const subsettingName = action.payload.subsettingName || action.payload.subSettingName;
      const subSubsettingName = action.payload.subSubsettingName || action.payload.subSubSettingName;
      const subSubSubsettingName = action.payload.subSubSubsettingName || action.payload.subSubSubSettingName;
      const newValue = action.payload.value;

      const newCostSettings = {...state.generalSettings.costs}
      if (!subsettingName) {
        newCostSettings[settingName] = newValue;
      }

      
      else if (!subSubsettingName) {
        newCostSettings[settingName][subsettingName] = newValue;
      }

      else if (!subSubSubsettingName) {
        newCostSettings[settingName][subsettingName][subSubsettingName] = newValue;
      }

      else {
        newCostSettings[settingName][subsettingName][subSubsettingName][subSubSubsettingName] = newValue;
      }

      state.generalSettings.costs = newCostSettings;
    },
    decipherTravelCostsAfterWarning: (state, action) => {
      state = decipherUploadedTravelCostsReturnHelper(state, action.payload);
      if (action.payload.NewLocations) {
        action.payload.NewLocations.forEach((location) => {
          const locationName = location.locationName;
          const uuid = location.locationId;
          const newRow = {'locationName': locationName, 'UUID': uuid};
          state['data'] = addRowHelper({state: state, newRow: newRow, uuid: uuid, tableName: 'locations', addCostRow: false})['data'];
          state['geoJson'] = addRowGeoJsonHelper(state['geoJson'], uuid, newRow, 'locations');
        })
      }

      if (action.payload.NewVehicleTypes) {
        action.payload.NewVehicleTypes.forEach((vehicle) => {
          const newRow = {'vehicleType': vehicle.vehicleType, 'UUID': vehicle.vehicleTypeId};
          state['data'] = addRowHelper({state: state, newRow: newRow, uuid: vehicle.vehicleTypeId, tableName: 'vehicle_types', addCostRow: false})['data'];
        })
      }

      return state;
    },
    setOptimizationPending: (state, action) => {
      if (action.payload && action.payload.pending) {
        state.optimization['optimizationPending'] = true;
      } else {
        state.optimization['optimizationPending'] = false;
      }
    },
    updateTimeSettings: (state, action) => {
      const columnNames = action.payload.columnNames;
      const columnValues = action.payload.columnValues;
      const settingNames = action.payload.settingNames;
      const settingValues = action.payload.settingValues;
      const subSettingNames = action.payload.subSettingNames;
      const subSettingValues = action.payload.subSettingValues;
      const tableName = action.payload.tableName;
      
      if (columnNames) {
        columnNames.forEach((columnName, idx) => {
          state.tableSettings[tableName]['isViewable'][columnName] = columnValues[idx];
        })
      }

      if (settingNames) {
        settingNames.forEach((settingName, idx) => {
          if (!tableName || settingName === 'timeUnit' || settingName === 'timePeriodSpansDays') {
            state.generalSettings.time[settingName] = settingValues[idx];
          } else {
            state.generalSettings.time[tableName][settingName] = settingValues[idx];
            if (((settingName === 'usePickupStart' || settingName === 'usePickupEnd')) && settingValues[idx] === true) {
              state.generalSettings.time[tableName].usePickupExact = false;
            }
            if ((settingName === 'usePickupExact') && settingValues[idx] === true) {
              state.generalSettings.time[tableName].usePickupStart = false;
              state.generalSettings.time[tableName].usePickupEnd = false;
            }
            if (((settingName === 'useDropoffStart' || settingName === 'useDropoffEnd')) && settingValues[idx] === true) {
              state.generalSettings.time[tableName].useDropoffExact = false;
            }
            if ((settingName === 'useDropoffExact') && settingValues[idx] === true) {
              state.generalSettings.time[tableName].useDropoffStart = false;
              state.generalSettings.time[tableName].useDropoffEnd = false;
            }
          }
        })
      }

      if (subSettingNames) {
        subSettingNames.forEach((subSettingName, idx) => {
          state.generalSettings.time[tableName][subSettingName] = subSettingValues[idx];
        })
      }

      state = validateTimeUnitViewability(state);


    },
    changeTimeUnit: (state, action) => {
      const newTimeUnit = action.payload.newTimeUnit;
      state.generalSettings.time.timeUnit = newTimeUnit;
      const tableShouldUseTimeWindows = STATIC_MODEL_SETTINGS[state.activeModelType].toggleTimeWindows;

      Object.keys(tableShouldUseTimeWindows).forEach((tableName) => {
        if (tableShouldUseTimeWindows[tableName]) {
          TIME_WINDOW_FIELDS.forEach(({ fieldName, useKey, isExact }) => {
            applyTimeFieldVisibility(state, tableName, fieldName, useKey, isExact, newTimeUnit);
          });
        }
      });
      return state;
    }
  },
  extraReducers(builder) {

    builder
      .addCase(fetchTravelCosts.pending, (state, action) => {
        let newState = {...state}
        if (!newState.loading) { newState.loading = {} }
        newState.loading.fetchTravelCosts = true;
        /*
        Link the rows to their vehicle types before the request goes out, not
        after it comes back.

        Every calculation merges its result onto the rows already here, keyed on
        the pair plus `vehicleTypeId || vehicleType`. A row that carries only the
        name and its calculated replacement, which carries the id, therefore look
        like different rows: nothing matches, and "Calculate missing" replaces a
        value someone typed with a computed one — silently, because the same
        mismatch hides it from countReplacedTravelCostPairs. Repairing here
        covers every caller of this thunk, the Populate button included.
        */
        reconcileTravelCostTyping(newState, { notify: action.meta?.arg?.silent !== true });
        state =  newState;
      })

      .addCase(fetchTravelCosts.fulfilled, (state, action) => {
        let newState = {...state};
        if (!newState.loading) { newState.loading = {} }
        newState.loading.fetchTravelCosts = false;

        const costMatrixResponse = CostMatrixResponse.constructFromObject(action.payload);
        const costMatrixItems = costMatrixResponse['travelCostResponses'] || [];
        let newTravelCosts = costMatrixItems.map(item => {
            // status: 's' success, 'e' error, 'w' warning (fallback method).
            // A missing status on a computed row is still a success — only 'e'
            // and 'w' are non-valid, so null/undefined must not fall through to
            // 'warning' (which flagged every geo-calculated row).
            let validStatus;
            if (item.status === 'e') { validStatus = false; }
            else if (item.status === 'w') { validStatus = 'warning'; }
            else { validStatus = true; }
            return {
              'fromLocationId': item.fromLocationId,
              'toLocationId': item.toLocationId,
              'fromLocationName': item.fromLocationName,
              'toLocationName': item.toLocationName,
              'cost': item.cost,
              'distance': item.distance,
              'duration': item.duration,
              'vehicleType': item.vehicleType || null,
              'vehicleTypeId': item.vehicleTypeId || null,
              'UUID': generateUUID(),
              'validationMessage': item.errorMessage ? [item.errorMessage] : [],
              'Valid': validStatus,
              // The method each field was calculated with (from the thunk), for
              // spotting a mix when gaps are filled later. Only on fields that have
              // a value. Not sent to /problem: TravelCost drops unknown fields.
              'costMethod': Object.fromEntries(
                Object.entries(action.meta?.costMethods || {})
                  .filter(([field]) => item[field] !== null && item[field] !== undefined && item[field] !== '')
              ),
            }
          }
        )
        // `silent` suppresses the success toast for automatic recomputes
        // (e.g. the cartesian pre-solve refresh) — errors still notify below.
        const silent = action.meta?.arg?.silent === true;

        // Guard against a "successful" response that carries no usable numbers.
        // /cost_matrix can return 200 with every distance/duration empty — most
        // often when the road-network (driving) engine is unreachable (server-side
        // enforcement of that premium method is deliberately off, so the request
        // still runs), but also for otherwise-uncomputable inputs. The old code
        // wrote those blank rows over the table AND still showed "successfully
        // populated", so the user saw empty cells under a green success toast with
        // no idea why. Detect the all-empty case, leave existing costs untouched,
        // and say what happened instead of claiming success.
        /*
        What THIS response calculated, judged before any previous value is merged
        back in. The merge (below) can put old numbers into the new rows, so
        asking the merged rows "did anything get calculated?" answers yes for a
        response that calculated nothing — and the straight-line notice would then
        appear over no new numbers at all. A response that computed nothing leaves
        the table alone, exactly as it did before the merge existed.
        */
        const responseComputedValue = hasAnyComputedTravelCost(newTravelCosts);

        // "Calculate missing and solve" asks for gaps only: keep every value the
        // table already had and take the new calculation for the empty pairs.
        // Pairs THIS response fell back on, so the message below counts what just
        // happened rather than every row still flagged from an earlier attempt.
        const keptPairKeys = new Set();
        if (action.meta?.arg?.fillMissingOnly === true) {
          newTravelCosts = keepExistingTravelCosts(state.data.travel_costs, newTravelCosts, state.generalSettings.costs, state.data.vehicle_types);
        } else if (responseComputedValue) {
          // A full recalculation replaces values, but never with nothing: a pair
          // the calculation couldn't measure keeps the value it had.
          newTravelCosts = preferCalculatedTravelCosts(state.data.travel_costs, newTravelCosts, keptPairKeys);
        }

        // A chosen road-network method replaced by straight-line (plan limit):
        // said as part of this calculation's one message, with "See plans".
        const straightLineFallback = responseComputedValue ? action.meta?.straightLineFallback : null;
        // Multipliers not applied to costs entered by hand: said once per scenario.
        const multipliersNotice = responseComputedValue ? action.meta?.multipliersNotice : null;
        // Run calculating an empty matrix on the person's behalf: its sentence
        // replaces the generic success, rather than adding a second toast.
        const autoCalculated = action.meta?.arg?.auto === true;

        if (!responseComputedValue) {
          if (!silent) {
            newState['notifications']['routine'].push({
              type: 'warning',
              message: 'No travel costs were returned — nothing was calculated. If you used the road-network (driving) method, the routing service may be temporarily unavailable; try a straight-line method in Cost settings, or try again.',
            });
          }
        } else {
          newState['data']['travel_costs'] = newTravelCosts;
          /*
          /cost_matrix returns the rows it was given, so a matrix sent typed
          comes back typed. It is still a wholesale replacement of the table,
          which makes it a writer like any other: reconcile so a response that
          carried only the type name (or none) cannot leave the table half
          linked, and so the column's visibility still matches the rows.
          */
          reconcileTravelCostTyping(newState, { notify: !silent });
          const travelCostErrors = costMatrixResponse['TravelCostErrors'] || [];
          // Rows the backend flagged (Valid==='warning' fallback, or ===false
          // error — e.g. a location unreachable by road). A matrix that computed
          // *something* but flagged rows must NOT report a plain "success": show a
          // warning banner so the user knows to review and fix those rows (they
          // can filter the table by status).
          const problems = calculatedTravelCostProblems(newTravelCosts, keptPairKeys);
          if (!silent) {
            if (travelCostErrors.length > 0) {
              travelCostErrors.forEach((err) => {
                newState['notifications']['routine'].push({
                  type: 'warning',
                  message: err.errorDescription,
                });
              });
            } else if (problems.emptyPairs > 0 || problems.keptPairs > 0 || problems.estimatedPairs > 0) {
              // Counted in location pairs, as the Run dialog counts them. Empty
              // pairs and straight-line estimates are different news, so they get
              // different words: an empty pair was not estimated at all.
              if (problems.emptyPairs > 0) {
                newState['notifications']['routine'].push({
                  type: 'warning',
                  message: `Travel costs populated, but ${problems.emptyPairs} of ${problems.totalPairs} location pairs couldn't be calculated, so they're still empty. Check those locations' coordinates; filter the table by status to find them.`,
                });
              }
              if (problems.keptPairs > 0) {
                newState['notifications']['routine'].push({
                  type: 'warning',
                  // A kept value can be stale: a location can be moved without
                  // its costs being cleared. Saying "populated" over it would be
                  // wrong, so these pairs are named and flagged in the table.
                  message: `Travel costs populated, but ${problems.keptPairs} of ${problems.totalPairs} location pairs couldn't be recalculated, so they kept their previous values — which may be out of date. Filter the table by status to review them.`,
                });
              }
              if (problems.estimatedPairs > 0) {
                newState['notifications']['routine'].push({
                  type: 'warning',
                  // Name the reason. The self-hosted road network covers North
                  // America, so every stop outside it falls back to straight-line
                  // — which without this reads as a broken feature rather than a
                  // documented limit. Two people who build this product both
                  // misread it that way from the outside.
                  message: `Travel costs populated, but ${problems.estimatedPairs} of ${problems.totalPairs} location pairs couldn't be measured along real roads, so they use straight-line estimates instead. Road-network distances currently cover North America. Filter the table by status to review those rows.`,
                });
              }
            }
            /*
            One message for the outcome of this calculation, not three. A first
            Run used to raise "successfully populated", "we calculated them for
            you" and the straight-line notice at once: with maxSnack=3 that
            buried the run's own toasts and read as three alerts in a row.
            Anything genuinely different (empty, kept or estimated pairs, above)
            still gets its own warning.
            */
            const headline = autoCalculated
              ? AUTO_CALCULATED_COSTS_MESSAGE
              : (problems.emptyPairs + problems.keptPairs + problems.estimatedPairs === 0 ? 'Travel costs successfully populated' : null);
            const outcome = [headline, straightLineFallback, multipliersNotice].filter(Boolean).join(' ');
            if (outcome) {
              newState['notifications']['routine'].push({
                type: straightLineFallback || multipliersNotice || autoCalculated ? 'info' : 'success',
                message: outcome,
                ...(straightLineFallback ? { upgrade: true } : {}),
              });
            }
          }
        }

        state = newState
      })

      .addCase(fetchTravelCosts.rejected, (state, action) => {
        var newState = {...state}
        if (!newState.loading) { newState.loading = {} }
        newState.loading.fetchTravelCosts = false;

        // A plan-limit rejection (e.g. road-network distances on a free plan)
        // is not a server outage — showing "could not connect... notified our
        // engineers" is both wrong and a dead end. Surface the actual reason
        // and offer an upgrade instead. `code`/`message` survive RTK's error
        // serialization, and the message is already the friendly, feature-aware
        // one baked in at throw time (see functions/server/backendError.js).
        const err = action.error || {};
        const newNotification = err.code === 'PLAN_LIMIT_EXCEEDED'
          ? {
              type: 'error',
              message: err.message || 'This scenario uses a feature that isn\'t included in your plan.',
              upgrade: true,
            }
          : {
              type: 'error',
              message: 'Unable to auto-populate travel costs. Could not connect to data server. We have notified our engineers of the problem.',
            };
        newState['notifications']['routine'].push(newNotification);
        state = newState;
      })
      
      // Note: run submission/failure is tracked in uiReducer (session run tracker)
      // and surfaced by the solve poller with a per-run message. Here we only apply
      // a finished run's results (below) and reset on a results-load failure.
      .addCase(fetchRunResults.rejected, (state) => {
        failSolveOptimizationHelper(state);
      })
      .addCase(fetchRunResults.fulfilled, (state, action) => {
        // Applies a finished run's results ({ problemResult, resultSummary }).
        if (!action.payload || !action.payload.problemResult) {
          failSolveOptimizationHelper(state);
          return;
        }
        const newState = {...state};
        newState['geoJson'] = clearGeoJsonResults(newState.geoJson);
        const resultSummary = action.payload.resultSummary;
        newState['data']['resultSummary'] = resultSummary;
        const problemResult = action.payload.problemResult;
        newState['data']['route_stops'] = problemResult.routeStopsFull || [];

        if (newState['data']['route_stops'].length > 0) {
          if (Object.keys(newState['data']['route_stops'][0]).indexOf('Valid') < 0) {
            for (var i = 0; i < newState['data']['route_stops'].length; i++) {
              newState['data']['route_stops'][i]['Valid'] = true
              newState['data']['route_stops'][i]['validationMessage'] = null
            }
          }
        }
        newState['data']['dropped_locations'] = problemResult.unassignedLocationsFull || [];
        newState['data']['dropped_trips'] = problemResult.unassignedTripsFull || [];

        // Capture the pristine solver output as the result-editing baseline
        // (after the Valid/validationMessage stamping above, so baseline rows
        // match the live rows field-for-field), and reset any amendments from
        // a previous run. Shallow row copies: immer's copy-on-write protects
        // deeper structure, and helpers never mutate baseline rows.
        newState['data']['resultBaseline'] = {
          route_stops: newState['data']['route_stops'].map((r) => ({ ...r })),
          dropped_trips: newState['data']['dropped_trips'].map((r) => ({ ...r })),
          dropped_locations: newState['data']['dropped_locations'].map((r) => ({ ...r })),
          resultSummary: resultSummary ? { ...resultSummary } : {},
        };
        newState['data']['resultAmendments'] = [];

        newState['generalSettings']['mapping']['mapMode'] = 'view';
        newState['geoJsonFilter']['locationLayerVisible'] = false;
        newState.geoJsonFilter['resultsLayerVisible'] = true;
        
        newState['optimization']['currentRuns'].unshift(new Date().toLocaleString());
        state = newState

        state.geoJsonFilter['vehicleIds'] = [];
        const drops = [...newState['data']['dropped_locations'], ...newState['data']['dropped_trips']];
        if (drops.length > 0) {
          newState.geoJsonFilter['dropLayerVisible'] = true;
        } else {
          newState.geoJsonFilter['dropLayerVisible'] = false;
        }
        state['geoJson'] = addGeoJsonResults(state['geoJson'], problemResult.paths, drops, newState['data']['locations']);
        const newNotification = {
          'type': 'success',
          'message': 'Optimization completed successfully'
        }
        state['notifications']['optimization'].push(newNotification)

      })
      .addCase(fetchAddress.fulfilled, (state, action) => {
        const uuid = action.payload.uuid;
        const address = action.payload.address;
        state = editRowHelper(state, uuid, 'locationName', 'locations', address, true)
        updateTableHelper(state['geoJson'], 'locations', 'locationName', uuid, address);
      })
      .addCase(fetchAddress.rejected, () => {
      })

      // Track the in-flight scenario load. Until it resolves the store still holds
      // the *previous* scenario, so without this flag the dashboard renders stale
      // data (the last example's tables and map) while the new one is fetched.
      .addCase(fetchSavedScenario.rejected, (state) => {
        if (!state.loading) { state.loading = {} }
        state.loading.fetchSavedScenario = false;
      })

      .addCase(fetchSavedScenario.pending, (state) => {
        if (!state.loading) { state.loading = {} }
        state.loading.fetchSavedScenario = true;
      })

      .addCase(fetchSavedScenario.fulfilled, (state, action) => {
        /*
        Load the fetched scenario into the state, but do not modify 
        the existing settings. Known limitation: saved files include settings.
        */
        state = initializeData({...action.payload.response,
                                loginData: state.loginData,
                                notifications: state.notifications,
                                geoJsonFilter: state.geoJsonFilter,
                              });
        state.data.resultSummary = action.payload.response?.data?.resultSummary;
        // Non-table result-editing keys survive the save verbatim but are
        // reset to [] by initializeDataHelper (same as resultSummary above) —
        // re-attach them so a saved scenario's edit history reloads intact.
        // Legacy saves lack the keys; fall back to the empty seeds.
        state.data.resultBaseline = action.payload.response?.data?.resultBaseline ?? {};
        state.data.resultAmendments = Array.isArray(action.payload.response?.data?.resultAmendments)
          ? action.payload.response.data.resultAmendments
          : [];
        const exampleMode = action.payload.exampleMode
        // The id this scenario was loaded BY, never the one its stored body
        // names (an older save's), and none for an example. See scenarioIdentity.js.
        delete state.generalSettings.scenarioId;
        if (state.generalSettings.scenario) {
          state.generalSettings.scenario.scenarioId = scenarioIdAfterLoad(action.payload);
        }
        // Millisecond precision so loading two scenarios within the same second
        // still produces a distinct value — this drives the map's fit-to-bounds
        // effect, which otherwise wouldn't re-fire on a quick example switch.
        state.generalSettings.scenario.scenarioChange = Date.now().toString();
        // Present only when this load came from the examples gallery; solves
        // send it so the backend grants the example's entitlement allowance
        // (see docs/backend-note-entitlements.md). Cleared by any other load.
        state.generalSettings.scenario.exampleSlug = action.payload.exampleSlug ?? null;
        if (exampleMode) {
          state.activeMode = 'example'
        }
        state.generalSettings.scenario.lastSaved = new Date().toString();

        if (action.payload.fromScratch) {
            state['data'] = INITIAL_DATA;
            state.geoJson = initializeGeoJson(INITIAL_DATA);
            /*
            "Build from scratch" on an example card starts the person's OWN
            scenario from the example's settings. An example's travel model
            (averageSpeed, roadCircuityFactor; backend #361) describes that
            example's city and fleet, can't be seen or edited on the site, and
            would be saved with the new scenario for good: usa-capitals' 55 / 1.18
            makes every travel time 1.94x shorter, and on an x-y card travel time
            becomes 2.5x distance. So it is left behind, and the new scenario uses
            the backend's defaults like any other (see costMethods.travelModel).
            "Use as template" keeps it on purpose: same city, same fleet
            (startTemplateFromExample).
            */
            dropExampleTravelModel(state);
        }

        // initializeData returns a fresh `loading: {}`, but be explicit so the flag
        // can't outlive the request that set it.
        if (!state.loading) { state.loading = {} }
        state.loading.fetchSavedScenario = false;

        return state
      })

      .addCase(decipherUploadedTravelCosts.rejected, () => {
      })
      .addCase(decipherUploadedTravelCosts.pending, () => {
      })
      .addCase(decipherUploadedTravelCosts.fulfilled, (state, action) => {
        const newLocations = action.payload.NewLocations;
        const newVehicleTypes = action.payload.NewVehicleTypes;

        if ((newLocations && newLocations.length > 0) || (newVehicleTypes && newVehicleTypes.length > 0)) { 
          let warningMessage = 'Unexpected values found in your Travel Cost upload file:\n'; 
          if (newLocations) {
            warningMessage += '\tThere were ' + newLocations.length + ' locations in your file upload that are not currently in your Locations table.\n'
          }
          if (newVehicleTypes) {
            warningMessage += '\tThere were ' + newVehicleTypes.length + ' vehicle types added that are not currently in your Vehicles table.\n'
          }

          if (newLocations && newVehicleTypes) {
            warningMessage += 'To add these items to your tables, select Proceed. To cancel your upload, press Cancel.'
          } else if (newLocations) {
            warningMessage += 'To add these new locations to the Location table, select Proceed. To cancel your upload, press Cancel.'
          } else if (newVehicleTypes) {
            warningMessage += 'To add these new new vehicle types to the Vehicle Types table, select Proceed. To cancel your upload, press Cancel.'
          }
          const newNotification = {
            'type': 'warning',
            'message': warningMessage,
            'action': 'decipherUploadedTravelCostPayload',
            'payload': action.payload
          }
          state.notifications.warning.push(newNotification);
        }
        
        else {
          state = decipherUploadedTravelCostsReturnHelper(state, action.payload);
          return state;

        }
      })
      .addCase(deleteOptimizationResult.rejected, () => {
      })
      .addCase(deleteOptimizationResult.pending, () => {
      })
      .addCase(deleteOptimizationResult.fulfilled, (state, action) => {
        const success = action.payload.success;
        if (success) {
          const newNotification = {
            'type': 'success',
            'message': 'Optimization result successfully deleted.'
          }
          state['notifications']['routine'].push(newNotification)
          state['optimization']['currentRuns'].unshift(new Date().toLocaleString())
        }
        return state
      })
      .addCase(deleteSavedScenario.rejected, () => {
      })
      .addCase(deleteSavedScenario.pending, () => {
      })
      .addCase(deleteSavedScenario.fulfilled, (state, action) => {
        const success = action.payload.success;
        if (success) {
          const newNotification = {
            'type': 'success',
            'message': 'Scenario successfully deleted.'
          }
          state['notifications']['routine'].push(newNotification)
          state['optimization']['savedScenarios'].unshift(new Date().toLocaleString())
        }
        return state
      })
      .addCase(saveScenario.rejected, (state, action) => {
        console.error('Failed to save scenario:', action.payload)
      })
      .addCase(saveScenario.pending, () => {
      })
      .addCase(saveScenario.fulfilled, (state, action) => {
        // See scenarioIdentity.js (ID_CLEARED_BY_UPDATE).
        const newNotification = action.payload.savedAsNewAfterUpdate
          ? { 'type': 'info', 'message': SAVED_AS_NEW_AFTER_UPDATE_MESSAGE }
          : { 'type': 'success', 'message': 'Scenario successfully saved.' }
        state['notifications']['routine'].push(newNotification)
        state['optimization']['savedScenarios'].unshift(new Date().toLocaleString())
        // Only onto the scenario this save was for: another may have been opened
        // while the request was out. The undo history gets the same stamp in the
        // wrapper at the bottom of this file. See scenarioIdentity.js.
        if (isSameOpenScenario(state, action.payload.identityBefore)) {
          delete state.generalSettings.scenario[ID_CLEARED_BY_UPDATE]
          state.generalSettings.scenario.scenarioId = action.payload.scenarioId
          state.generalSettings.scenario.scenarioName = action.payload.scenarioName
        }
        return state
      })

      /*
      A rename that failed used to do nothing at all: the name looked changed in
      the list and wasn't. Its callers dispatch and move on (none of them catch),
      so this is the one place it is reported. A backend error's message is
      already written for a person (functions/server/backendError.js); one without
      a code came from the client and isn't, so a plain sentence stands in.
      */
      .addCase(renameScenario.rejected, (state, action) => {
        state.notifications.routine.push({ type: 'error', message: renameFailedMessage(RENAME_SCENARIO_FAILED, action.error) });
      })
      .addCase(renameScenario.pending, () => {
      })
      .addCase(renameScenario.fulfilled, (state, action) => {
        const newNotification = {
          'type': 'success',
          'message': 'Scenario successfully renamed.'
        }
        state['notifications']['routine'].push(newNotification)
        state['optimization']['savedScenarios'].unshift(new Date().toLocaleString())
        // Renaming another saved scenario from the list must not retitle the open
        // one, nor may a rename that lands after a different scenario was opened.
        if (action.payload.renamedOpenScenario && isSameOpenScenario(state, action.payload.identityBefore)) {
          state.generalSettings.scenario.scenarioName = action.payload.scenarioName;
        }
        return state
      })
      .addCase(renameTrial.rejected, (state, action) => {
        state.notifications.routine.push({ type: 'error', message: renameFailedMessage(RENAME_RUN_FAILED, action.error) });
      })
      .addCase(renameTrial.pending, () => {
      })
      .addCase(renameTrial.fulfilled, (state) => {
        const newNotification = {
          'type': 'success',
          'message': 'Run successfully renamed.'
        }
        state['notifications']['routine'].push(newNotification)
        state['optimization']['currentRuns'].unshift(new Date().toLocaleString())
        return state
      })
      .addCase(submitMessage.rejected, () => {
      })
      .addCase(submitMessage.pending, () => {
      })
      .addCase(submitMessage.fulfilled, () => {
      })
      .addCase(submitBugReport.rejected, () => {
      })
      .addCase(submitBugReport.pending, () => {
      })
      .addCase(submitBugReport.fulfilled, () => {
      })
    }

})

export const { onLocationDataChange, addLocationRow,
  deleteRow, addRow, addRowFromMap, addGeoJsonTrip, addRows, updateTable,
  addRowGeoJson, updateActiveModelType, updateCoordinateSystem,
  clearNotifications, addNotification, updateModelSetting,
  updateTableSetting, updateGeneralSetting, clearAllTableData, clearTableData, clearResults,
  applyResultAmendment, revertResultAmendments,
  setScenario, addFilter, removeFilter, updateFilter,
  setActiveMode, startTemplateFromExample, toggleTableSetting, toggleCostVehicleTypeDependence,
  repairTravelCostTyping,
  updateTimeSettings, updateTableMulti, rescaleCalculatedTravelCosts, decipherTravelCostsAfterWarning,
  setOptimizationPending, deleteRowGeoJson, updateCostSettings,
  login, logoutUser, clearColumn, buildFromScratch, setMapMode,
  changeTimeUnit, setUserLimits, updateMapMode, setUserPlan, clearPendingClaim
} = inputSlice.actions

export const selectPendingClaimUserId = (state) => state.inputData.present.loginData.pendingClaimUserId ?? null;

export const selectGeneralSettings = state => {return state.inputData.present.generalSettings}; 
export const selectColumnMappingSettings = (state) => {return state.inputData.present.generalSettings.columnMapping}
export const selectGeneralSetting = (settingName) => state => {return state.inputData.present.generalSettings[settingName]};



// Memoized no-arg variant: concatenating the buckets built a fresh array on
// every call, which re-rendered NotificationManager (and re-ran its effect) on
// every store dispatch — including each solve-status poll. Keyed on the
// notifications object itself, which only changes identity when a notification
// is actually added or cleared.
const selectAllNotifications = createSelector(
  [state => state.inputData.present['notifications']],
  (notifications) => Object.keys(notifications).reduce(
    (all, key) => all.concat(notifications[key]), []
  )
);

export const selectNotifications = (type) => {
  if (!type) return selectAllNotifications;
  // Typed form returns the bucket by reference — already stable.
  return state => state.inputData.present['notifications'][type];
}

export const selectDefaultValue = (tableName, columnName) => (state) => {
  const defaultValue = getDefaultValue(state.inputData.present, tableName, columnName);
  return defaultValue
}


/**
 * returns the list of current optimization runs. This is used to update components that need
 * updating each run.
 */
export const selectOptimizationRuns = (state) => {
  return state.inputData.present.optimization.currentRuns
}

/**
 * returns the date of the last saved scenario load
 */
export const selectScenarioChange = (state) => {
  return state.inputData.present.generalSettings.scenario && state.inputData.present.generalSettings.scenario.scenarioChange;
}

export const getFetchingTravelCosts = state => {
  return state.inputData.present.loading && state.inputData.present.loading.fetchTravelCosts;
}

/** True while a saved scenario / example is being fetched. Until it resolves the
 *  store still holds the previously loaded scenario. */
export const selectFetchingScenario = state => {
  return Boolean(state.inputData.present.loading && state.inputData.present.loading.fetchSavedScenario);
}


/**
 * returns the list of saved scenarios. This is used to update components that need
 * updating when saved scenarios are changed.
 */
export const selectSavedScenarios = (state) => {
  return state.inputData.present.optimization.savedScenarios
}


export const selectActiveResultsTableName = state => {
  const activeModelType = state.inputData.present.activeModelType;
  const resultsTable = STATIC_MODEL_SETTINGS[activeModelType]['resultsTable'];
  return resultsTable;
}


export const selectResultTime = ({selectMinimum=true}) => state => {
  const activeModelType = state.inputData.present.activeModelType;
  const resultsTable = STATIC_MODEL_SETTINGS[activeModelType]['resultsTable'];
  if (state.inputData.present.data[resultsTable].length === 0) {
    return null
  }
  const timeType = state.inputData.present.generalSettings.time.timeUnit;
  const columnIndicator = timeType === 'int' || timeType === 'integer' ? 'columnNameInt' : 'columnNameTime';
  const columnTypeIndicator = selectMinimum ? 'resultsArrival' : 'resultsDeparture';

  const timeColumn = STATIC_TABLE_SETTINGS[resultsTable]['timeWindows'][columnTypeIndicator][columnIndicator];

  const timeValues = state.inputData.present.data[resultsTable].map(row => {return row[timeColumn]});

  if (selectMinimum) {
    return Math.min(...timeValues);
  }
  return Math.max(...timeValues);
  
}


export const selectDistanceCalculationNeeded = state => {
  /**
   * This function determines whether distance needs to be calculated as an
   * input. This is the case if:
   * 1. The user has selected to use distance as the cost metric
   */
  return distanceCalculationNeeded(state.inputData.present);
}

export const distanceCalculationNeeded = state => {
  /**
   * This calculates whether distance needs to be calculated as an
   * input. It differs from the selectDistanceCalculationNeeded in that
   * it is not a selector, so it takes the current state as input.
   */
  return state.generalSettings.costs.costMetric === 'distance';
}


export const selectDurationCalculationNeeded = state => {
  /**
   * This function determines whether duration needs to be calculated as an
   * input. This is the case if:
   * 1. The user has selected to use time windows for any table
   * 2. The user has selected to use duration as the cost metric
   */
  return durationCalculationNeeded(state.inputData.present);
}

export const durationCalculationNeeded = state => {
  /**
   * This calculates whether duration needs to be calculated as an
   * input. It differs from the selectDurationCalculationNeeded in that
   * it is not a selector, so it takes the current state as input.
   */
  const activeModelSettings = STATIC_MODEL_SETTINGS[state.activeModelType];
  for (const tableName of ['trips', 'locations', 'vehicles']) {
    if (activeModelSettings.toggleTimeWindows[tableName] &&
        state.generalSettings.time[tableName]?.useTimeWindows) {
      return true;
    }
  }
  return state.generalSettings.costs.costMetric === 'duration';
}

/*
The single source of truth for "what does Undo undo". redux-undo filters its
history with it, and store.js's action-history middleware mirrors it so the two
can never drift — see the comment on that middleware for what drift cost us.
*/
export const includeActions = [
  "inputData/applyResultAmendment",
  "inputData/revertResultAmendments",
  // Applying a run's results is an undo boundary. Without it redux-undo's
  // _latestUnfiltered never syncs to the solved state (this reducer was
  // filtered), so undoing the FIRST result edit would leap past the results
  // to whatever the last included action produced — pre-solve emptiness.
  // (solveOptimization/fulfilled below is the legacy entry from when that
  // action itself carried the results.)
  "posts/fetchRunResults/fulfilled",
  "inputData/addRow",
  "inputData/addRowFromMap",
  "inputData/onLocationDataChange",
  "inputData/addLocationRow",
  "inputData/deleteRow",
  "inputData/addGeoJsonTrip",
  "inputData/addRowGeoJson",
  "inputData/addRows",
  "inputData/updateTable",
  "inputData/fetchTravelCosts",
  "inputData/clearAllTableData",
  "inputData/clearTableData",
  "inputData/clearResults",
  // One step for the whole example-to-template clone, so Undo restores the example.
  "inputData/startTemplateFromExample",
  "inputData/setScenario",
  "inputData/updateTableMulti",
  // The Run dialog's "Rescale those costs and solve": its own step, so one Undo
  // restores exactly the costs it changed.
  "inputData/rescaleCalculatedTravelCosts",
  "inputData/updateGeneralSetting",
  // Undoable because switching to geographic can clear out-of-range
  // coordinates — Undo is the recovery path for that.
  "inputData/updateCoordinateSystem",
  'posts/solveOptimization/fulfilled',
  'solveOptimization'
]

const undoableInputReducer = undoable(inputSlice.reducer, 
  {filter: 
    function filterActions(action) {
      return includeActions.includes(action.type);
    }
, limit:10});

/*
A saved or renamed scenario's id and name also go onto its undo history.

The id lives in undoable state, but a save's fulfilled action is not an undo
step, so Undo after a save brought back a snapshot from before it: a null id (the
next Save made a duplicate) or, after Save New, the original scenario's id (the
next Save wrote over the original). Only snapshots of the same open scenario are
stamped, so an id never crosses a load. includeActions is untouched (the history
middleware in store.js mirrors it). See stampScenarioHistory.
*/
export default function inputDataReducer(state, action) {
  const next = undoableInputReducer(state, action);
  const payload = action && action.payload;
  if (action.type === saveScenario.fulfilled.type && payload) {
    return stampScenarioHistory(next, payload.identityBefore,
      { scenarioId: payload.scenarioId, scenarioName: payload.scenarioName });
  }
  if (action.type === renameScenario.fulfilled.type && payload && payload.renamedOpenScenario) {
    return stampScenarioHistory(next, payload.identityBefore,
      { scenarioId: payload.scenarioId, scenarioName: payload.scenarioName });
  }
  return next;
}
